import React from 'react';
import classNames from 'classnames';
import './styles.css';

/**
 * Props for {@link IconButton}.
 *
 * - onClick: handler for the onClick (a no-op when `disabled`).
 * - active: is the button active (adds an 'active' class to the wrapper div).
 * - disabled: is the button disabled (adds a 'disabled' class to the wrapper
 *   div and makes onClick a no-op).
 * - variant: if set a `icon-button-${variant}` class is added to the wrapper
 *   div.
 * - title: the title (help text shown on long hover) for the button.
 */
export interface IconButtonProps {
  children?: React.ReactNode;
  className?: string;
  onClick: (evt: React.MouseEvent<HTMLDivElement>) => void;
  active?: boolean;
  disabled?: boolean;
  variant?: string;
  title?: string;
}

/**
 * Base wrapper geometry (was `.icon-button`). The SVG recolour-on-hover and the
 * disabled dimming keyed off the literal `disabled` class live in `styles.css`
 * (the cursor swap is the `[&:not(.disabled):hover]:cursor-pointer` utility).
 */
const BASE =
  'icon-button flex h-[37px] w-[37px] cursor-default select-none items-center justify-center [&:not(.disabled):hover]:cursor-pointer';

/**
 * Per-variant class strings. Each value is a WHOLE literal string so Tailwind's
 * content scanner sees the utilities, and so the literal `icon-button-${variant}`
 * hook stays in the DOM for the descendant SVG-recolour rules in `styles.css`
 * (and the cross-component `.icon-button-floating` reach from Toolbar). The
 * variant prop is free-form, so unknown variants fall back to the base only.
 */
const VARIANT_CLASSES: Record<string, string> = {
  appbar: 'icon-button-appbar h-[45px] w-[45px]',
  panel: 'icon-button-panel',
  'panel-dropdown': 'icon-button-panel-dropdown',
  floating: 'icon-button-floating',
  inline: 'icon-button-inline h-[15px] w-[15px]',
  narrow: 'icon-button-narrow w-[18px]',
};

/**
 * The label+icon pill (was `.icon-with-label`). Callers pass the literal
 * `icon-with-label` className (AttributesInspector, MPCInspector); detect it
 * and append the pill utilities as a whole literal string so the auto sizing,
 * padding, border and svg clamp win over the variant geometry. Hover colours
 * approximate the old `lighten/darken($primary)` outputs (no exact token).
 */
const ICON_WITH_LABEL =
  'h-auto w-auto rounded text-grey2 px-2 py-[5px] border border-[#e0e2e5] ' +
  'hover:text-[#0f479d] hover:bg-[#f9fbfe] hover:border-[#b3cef8] ' +
  '[&_svg]:ml-2.5 [&_svg]:-mr-px [&_svg]:h-[15px] [&_svg]:w-[15px]';

/**
 * Render a button with an icon. The icon should be passed in as a React child.
 */
const IconButton = ({ children, className, onClick, active, disabled, variant, title }: IconButtonProps) => {
  const variantClass = variant
    ? VARIANT_CLASSES[variant] ?? `icon-button-${variant}`
    : undefined;

  // `active` on the panel/panel-dropdown variants paints the active background
  // (was `.icon-button-panel.active`); the inset hairline keeps the 1px bottom
  // grey divider without shifting the box. The literal `active`/`disabled`
  // classes also stay so the SVG-recolour CSS and Toolbar's matching hooks fire.
  const activePanel = active && (variant === 'panel' || variant === 'panel-dropdown');

  // The pill variant is selected by the literal `icon-with-label` className the
  // call sites pass; its utilities are appended after the variant geometry so
  // the auto sizing wins.
  const withLabel = className?.split(' ').includes('icon-with-label');

  return (
    <div
      className={classNames(
        BASE,
        variantClass,
        {
          active,
          disabled,
          'opacity-50': disabled,
          'bg-primary hover:bg-primary-hover border-b border-grey shadow-[inset_0_-1px_0_0_var(--color-grey)]': activePanel,
        },
        className,
        { [ICON_WITH_LABEL]: withLabel },
      )}
      onClick={evt => {
        if (!disabled) {
          onClick(evt);
        }
      }}
      title={title}
    >
      {children}
    </div>
  );
};

export default IconButton;
